import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, Check, Sparkles, Code2, Palette, TrendingUp, Smartphone, ShoppingBag } from 'lucide-react';
import { FadeUp, TextReveal } from '@/components/site/Reveal';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { fetchOne, STATIC } from '@/lib/cms';
import SEO from '@/components/site/SEO';

const ICONS = { Sparkles, Code2, Palette, TrendingUp, Smartphone, ShoppingBag };

export default function ServiceDetail() {
  const { slug } = useParams();
  const [svc, setSvc] = useState(STATIC.services.find(s => s.slug === slug) || STATIC.services[0]);

  useEffect(() => {
    fetchOne('services', slug, STATIC.services).then(s => s && setSvc(s));
  }, [slug]);

  if (!svc) return null;
  const Icon = ICONS[svc.icon] || Sparkles;
  const related = STATIC.services.filter(s => s.slug !== svc.slug).slice(0, 3);

  return (
    <>
      <SEO title={svc.meta_title || `${svc.title} — Slinkflow`} description={svc.meta_description || svc.short} />

      <section className="relative overflow-hidden pt-40 pb-24 lg:pt-56 lg:pb-32">
        <div className="absolute -top-32 right-0 h-[520px] w-[520px] hero-gradient-orb" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Link to="/services" className="text-sm text-ink-500 link-underline">← All services</Link>
          <div className="mt-8 grid items-start gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <p className="text-xs uppercase tracking-[0.3em] text-cyan">{svc.category}</p>
              <h1 className="mt-6 font-display text-[56px] font-light leading-[1.2] tracking-[-0.03em] md:text-[88px] lg:text-[100px]">
                <TextReveal text={svc.title} />
              </h1>
              <p className="mt-6 max-w-3xl text-[20px] leading-relaxed text-ink-500">{svc.short}</p>
            </div>
            <div className="lg:col-span-4 rounded-3xl border border-ink-100 bg-white p-8">
              <Icon className="h-10 w-10 text-cyan" strokeWidth={1.5} />
              <p className="mt-6 text-xs uppercase tracking-[0.3em] text-ink-400">Starting from</p>
              <p className="mt-2 font-display text-5xl font-light text-ink-900">{svc.price_from ? `$${(svc.price_from/1000).toFixed(svc.price_from%1000===0?0:1)}k` : 'Custom'}</p>
              <Link to="/contact" className="btn-cyan mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-medium">
                Start a project <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid gap-10 lg:grid-cols-12">
            <FadeUp className="lg:col-span-5">
              <p className="text-xs uppercase tracking-[0.3em] text-cyan">Included</p>
              <h2 className="mt-4 font-display text-[40px] font-light leading-[1.05] md:text-[52px]">Everything you need to ship <span className="gradient-text font-medium">a category-defining experience.</span></h2>
            </FadeUp>
            <div className="lg:col-span-7 grid gap-4 sm:grid-cols-2">
              {(svc.features || []).map((f, i) => (
                <FadeUp key={f} delay={i * 0.04}>
                  <div className="flex items-start gap-3 rounded-2xl border border-ink-100 bg-white p-5">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-cyan" />
                    <span className="text-[15px] text-ink-900">{f}</span>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-ink-50 py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">Our process</p>
          <h2 className="mt-4 font-display text-[40px] font-light leading-[1.05] md:text-[52px]">A predictable path to <span className="gradient-text font-medium">excellent outcomes.</span></h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STATIC.process.slice(0, 6).map((p, i) => (
              <FadeUp key={p.step} delay={i * 0.04}>
                <div className="rounded-3xl bg-white p-8">
                  <p className="font-display text-sm text-cyan">{p.step}</p>
                  <h3 className="mt-2 font-display text-2xl text-ink-900">{p.title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-ink-500">{p.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-cyan">FAQ</p>
            <h2 className="mt-4 font-display text-[40px] font-light leading-[1.05]">Common <span className="gradient-text font-medium">questions.</span></h2>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="divide-y divide-ink-100 border-y border-ink-100">
              {STATIC.faqs.slice(0, 4).map((f, i) => (
                <AccordionItem key={f.id} value={`item-${i}`} className="border-0">
                  <AccordionTrigger className="py-6 text-left text-[18px] hover:text-cyan hover:no-underline">{f.question}</AccordionTrigger>
                  <AccordionContent className="pb-6 text-ink-500">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="bg-ink-50 py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <h2 className="font-display text-[40px] font-light">Related <span className="gradient-text font-medium">services</span></h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {related.map(r => {
              const RIcon = ICONS[r.icon] || Sparkles;
              return (
                <Link key={r.id} to={`/services/${r.slug}`} className="group block rounded-3xl bg-white p-8 transition hover:-translate-y-1 hover:shadow-hover">
                  <RIcon className="h-8 w-8 text-cyan" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display text-2xl text-ink-900">{r.title}</h3>
                  <p className="mt-2 text-[14px] text-ink-500">{r.tagline}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
